import { cn } from "@/lib/utils"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

const inputClass =
  "w-full h-11 px-4 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50 text-neutral-900 dark:text-white text-sm placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-brand-button/40 focus:border-brand-button/40 transition-all"

const labelClass = "block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-1.5"

type FieldLabelProps = {
  children: React.ReactNode
  required?: boolean
  hint?: string
}

export function FieldLabel({ children, required, hint }: FieldLabelProps) {
  return (
    <label className={labelClass}>
      {children}
      {required && <span className="text-red-500 ml-0.5">*</span>}
      {hint && <span className="text-neutral-400 dark:text-neutral-500 font-normal ml-1">({hint})</span>}
    </label>
  )
}

type NumberFieldProps = {
  label: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  required?: boolean
  hint?: string
  min?: number
  max?: number
  step?: number
}

export function NumberField({ label, value, onChange, placeholder, required, hint, min, max, step }: NumberFieldProps) {
  return (
    <div>
      <FieldLabel required={required} hint={hint}>{label}</FieldLabel>
      <input
        type="number"
        min={min}
        max={max}
        step={step}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={inputClass}
      />
    </div>
  )
}

type TextFieldProps = {
  label: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  required?: boolean
  hint?: string
  type?: string
}

export function TextField({ label, value, onChange, placeholder, required, hint, type = "text" }: TextFieldProps) {
  return (
    <div>
      <FieldLabel required={required} hint={hint}>{label}</FieldLabel>
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={inputClass}
      />
    </div>
  )
}

type SelectOption = {
  value: string
  label: string
}

type SelectFieldProps = {
  label: string
  value: string
  onChange: (value: string) => void
  options: SelectOption[]
  required?: boolean
  hint?: string
  placeholder?: string
  className?: string
}

export function SelectField({ label, value, onChange, options, required, hint, placeholder, className }: SelectFieldProps) {
  return (
    <div className={cn(className)}>
      <FieldLabel required={required} hint={hint}>{label}</FieldLabel>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-11 rounded-xl border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50 text-sm focus:ring-2 focus:ring-brand-button/40">
          <SelectValue placeholder={placeholder || "Select..."} />
        </SelectTrigger>
        <SelectContent className="rounded-xl border-neutral-200 dark:border-neutral-700">
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value} className="rounded-lg text-sm">
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

export function SubmitButton({ onClick, disabled, children }: { onClick: () => void; disabled?: boolean; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="w-full h-12 rounded-xl bg-primary-gradient text-primary-foreground font-semibold text-sm shadow-sm hover:brightness-[0.97] hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-sm"
    >
      {children}
    </button>
  )
}

export function ResetButton({ onClick, children }: { onClick: () => void; children?: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className="w-full h-11 rounded-xl border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 font-semibold text-sm hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-all"
    >
      {children || "Calculate Again"}
    </button>
  )
}
